import React, { useEffect, useMemo, useState } from 'react'
import ProjectShowcase from './components/ProjectShowcase'
import './App.css'

const GITHUB_USER = 'HowieDuhzit'

const focusAreas = [
  'Technical art systems',
  'Agent infrastructure',
  'Realtime 3D + WebGPU',
  'Creative developer tooling'
]

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(value)

function App() {
  const [profile, setProfile] = useState(null)
  const [repos, setRepos] = useState([])
  const [status, setStatus] = useState('loading')

  useEffect(() => {
    const controller = new AbortController()

    const loadGitHub = async () => {
      try {
        const [profileResponse, reposResponse] = await Promise.all([
          fetch(`https://api.github.com/users/${GITHUB_USER}`, {
            signal: controller.signal,
            headers: { Accept: 'application/vnd.github+json' }
          }),
          fetch(`https://api.github.com/users/${GITHUB_USER}/repos?per_page=100&sort=updated&type=owner`, {
            signal: controller.signal,
            headers: { Accept: 'application/vnd.github+json' }
          })
        ])

        if (!profileResponse.ok || !reposResponse.ok) {
          throw new Error('GitHub API unavailable')
        }

        const [profileData, repoData] = await Promise.all([
          profileResponse.json(),
          reposResponse.json()
        ])

        setProfile(profileData)
        setRepos(repoData.filter((repo) => !repo.fork && !repo.archived))
        setStatus('ready')
      } catch (error) {
        if (error.name !== 'AbortError') setStatus('error')
      }
    }

    loadGitHub()
    return () => controller.abort()
  }, [])

  const totals = useMemo(() => {
    return repos.reduce(
      (acc, repo) => ({
        stars: acc.stars + repo.stargazers_count,
        forks: acc.forks + repo.forks_count
      }),
      { stars: 0, forks: 0 }
    )
  }, [repos])

  const languages = useMemo(() => {
    const counts = repos.reduce((acc, repo) => {
      if (repo.language) acc[repo.language] = (acc[repo.language] || 0) + 1
      return acc
    }, {})

    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
  }, [repos])

  const lastUpdated = repos[0]?.updated_at

  return (
    <div className="site-shell">
      <div className="ambient-field" aria-hidden="true" />

      <header className="masthead">
        <a className="wordmark" href="#top" aria-label="Howie Duhzit home">
          <span className="wordmark-mark">H/D</span>
          <span className="wordmark-copy">Howie Duhzit</span>
        </a>
        <nav className="nav-strip" aria-label="Primary navigation">
          <a href="#work">Work</a>
          <a href="#signal">Signal</a>
          <a href="#contact">Contact</a>
        </nav>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow">Technical Artist · Developer · Sleepy Studio</p>
            <h1>
              I build <span>strange useful systems</span> where code, 3D, agents, and realtime graphics overlap.
            </h1>
            <p className="hero-deck">
              Portfolio data below is pulled directly from GitHub at runtime. No invented star counts, repo totals, proficiency meters, or placeholder projects.
            </p>
            <div className="hero-actions">
              <a className="primary-action" href="#work">Browse live work</a>
              <a className="secondary-action" href={`https://github.com/${GITHUB_USER}`} target="_blank" rel="noreferrer">GitHub ↗</a>
            </div>
          </div>

          <aside className="telemetry" aria-label="Live GitHub telemetry">
            <div className="telemetry-orbit" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
            <p className="telemetry-label">LIVE / GITHUB</p>
            {status === 'loading' && <p className="telemetry-state">Acquiring signal…</p>}
            {status === 'error' && <p className="telemetry-state telemetry-error">GitHub signal unavailable.</p>}
            {status === 'ready' && (
              <div className="telemetry-grid">
                <div><strong>{formatNumber(profile?.public_repos || 0)}</strong><span>public repos</span></div>
                <div><strong>{formatNumber(totals.stars)}</strong><span>stars</span></div>
                <div><strong>{formatNumber(totals.forks)}</strong><span>forks</span></div>
                <div><strong>{profile?.followers ?? 0}</strong><span>followers</span></div>
              </div>
            )}
          </aside>
        </section>

        <section className="signal-band" id="signal">
          <div className="signal-intro">
            <p className="eyebrow">Operating range</p>
            <h2>Not a stack. A working surface.</h2>
          </div>
          <div className="signal-chips">
            {focusAreas.map((area, index) => (
              <span key={area} style={{ '--i': index }}>{area}</span>
            ))}
          </div>
        </section>

        <section className="language-rack" aria-label="Most common repository languages">
          <div className="rack-heading">
            <span>Language frequency</span>
            <span>{status === 'ready' ? `${repos.length} active source repos sampled` : 'Live data pending'}</span>
          </div>
          <div className="rack-bars">
            {languages.map(([language, count], index) => (
              <div className="rack-row" key={language}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <strong>{language}</strong>
                <div className="rack-track"><i style={{ width: `${Math.max(18, (count / languages[0][1]) * 100)}%` }} /></div>
                <em>{count}</em>
              </div>
            ))}
          </div>
        </section>

        <section className="work-section" id="work">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Repository field</p>
              <h2>Current work, without curation theater.</h2>
            </div>
            {lastUpdated && (
              <p className="sync-note">Newest GitHub activity: {new Date(lastUpdated).toLocaleDateString()}</p>
            )}
          </div>
          <ProjectShowcase repos={repos} status={status} />
        </section>

        <section className="contact-section" id="contact">
          <div>
            <p className="eyebrow">Open channel</p>
            <h2>Build something that does not already have a template.</h2>
          </div>
          <div className="contact-links">
            <a href="mailto:contact@howieduhzit.best">contact@howieduhzit.best</a>
            <a href="https://www.linkedin.com/in/howieduhzit" target="_blank" rel="noreferrer">LinkedIn ↗</a>
            <a href={`https://github.com/${GITHUB_USER}`} target="_blank" rel="noreferrer">GitHub ↗</a>
          </div>
        </section>
      </main>

      <footer>
        <span>HOWIEDUHZIT.BEST</span>
        <span>Live GitHub data · static React frontend</span>
      </footer>
    </div>
  )
}

export default App
